import { useState } from 'react';

import { formatAxisValue, formatMetricValue, type MetricFormat } from '@/lib/metric-format';

import { ChartFrame, ChartLegend } from './chart-frame';
import {
  buildLinePath,
  buildTicks,
  computeExtent,
  formatAxisLabel,
  labelIndexes,
  niceCeil,
  shortenLabel,
  toFinite,
} from './chart-utils';

const WIDTH = 720;
const HEIGHT_POR_DEFECTO = 240;
const PADDING = { top: 24, right: 54, bottom: 30, left: 54 };

/** Cómo se dibuja una serie: línea o barras. */
export type DualAxisKind = 'line' | 'bar';

export interface DualAxisSeries {
  label: string;
  data: (number | null)[];
  /** Eje vertical al que pertenece la serie (izquierdo o derecho). */
  axis: 'left' | 'right';
  kind?: DualAxisKind;
  color?: string;
}

interface DualAxisChartProps {
  labels: (string | number)[];
  series: DualAxisSeries[];
  /** Título del eje izquierdo (se muestra junto a sus marcas). */
  leftTitle?: string;
  /** Título del eje derecho (se muestra junto a sus marcas). */
  rightTitle?: string;
  leftFormat?: MetricFormat;
  rightFormat?: MetricFormat;
  height?: number;
}

interface AxisScale {
  max: number;
  ticks: { value: number; y: number }[];
  valueY: (value: number) => number;
}

/** Escala vertical de un eje, siempre partiendo de cero (como el panel anterior). */
function buildAxisScale(maxValue: number, innerHeight: number): AxisScale {
  const max = maxValue > 0 ? niceCeil(maxValue) : 1;

  return {
    max,
    ticks: buildTicks(0, max, 4).map((value) => ({
      value,
      y: PADDING.top + innerHeight - (value / max) * innerHeight,
    })),
    valueY: (value: number) => PADDING.top + innerHeight - (value / max) * innerHeight,
  };
}

/**
 * Gráfico de doble eje (combo) en SVG, sin dependencias.
 *
 * Permite mezclar barras y líneas con escalas verticales independientes a cada
 * lado, replicando el patrón del panel anterior (ej. montos + galones, o
 * transacciones en barras + monto en línea). Cada eje parte de cero para que la
 * comparación visual siga siendo honesta.
 */
export function DualAxisChart({
  labels,
  series,
  leftTitle,
  rightTitle,
  leftFormat = 'number',
  rightFormat = 'number',
  height = HEIGHT_POR_DEFECTO,
}: DualAxisChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const innerHeight = Math.max(height - PADDING.top - PADDING.bottom, 10);
  const innerWidth = Math.max(WIDTH - PADDING.left - PADDING.right, 10);

  const colorOf = (item: DualAxisSeries): string =>
    item.color ?? (item.kind === 'bar' ? '#06b6d4' : '#10b981');

  const leftScale = buildAxisScale(computeExtent(series.filter((s) => s.axis === 'left')).max, innerHeight);
  const rightScale = buildAxisScale(computeExtent(series.filter((s) => s.axis === 'right')).max, innerHeight);

  const bandWidth = innerWidth / Math.max(labels.length, 1);
  const visibleLabels = labelIndexes(labels.length, 8);

  const barSeries = series.filter((item) => item.kind === 'bar');
  const lineSeries = series.filter((item) => item.kind !== 'bar');

  // Barras agrupadas: cada serie de barras usa la escala de su propio eje.
  const groupWidth = Math.min(bandWidth * 0.72, 40);
  const barWidth = groupWidth / Math.max(barSeries.length, 1);

  const bars = barSeries.map((item, seriesIndex) => {
    const scale = item.axis === 'left' ? leftScale : rightScale;
    const color = colorOf(item);

    const rects = labels.map((_, index) => {
      const value = toFinite(item.data[index]);
      if (value === null || value === 0) return null;

      const center = PADDING.left + bandWidth * (index + 0.5);
      const offset = -groupWidth / 2 + barWidth * seriesIndex + barWidth / 2;
      const top = scale.valueY(Math.max(value, 0));
      const bottom = scale.valueY(Math.min(value, 0));

      return {
        x: center + offset - barWidth / 2,
        y: top,
        width: Math.max(barWidth - 2, 1),
        height: Math.max(bottom - top, 1),
      };
    });

    return { label: item.label, color, rects };
  });

  const lines = lineSeries.map((item) => {
    const scale = item.axis === 'left' ? leftScale : rightScale;
    const color = colorOf(item);

    const points = labels.map((_, index) => {
      const value = toFinite(item.data[index]);
      return {
        x: PADDING.left + (innerWidth * index) / Math.max(labels.length - 1, 1),
        y: value === null ? null : scale.valueY(value),
      };
    });

    return { label: item.label, color, data: item.data, axis: item.axis, points, ...buildLinePath(points, false) };
  });

  const legendItems = series.map((item) => ({ label: item.label, color: colorOf(item) }));

  return (
    <ChartFrame legend={legendItems.length > 1 ? <ChartLegend items={legendItems} /> : null}>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label="Gráfico de doble eje"
        className="h-auto w-full"
      >
        {/* Títulos de los ejes */}
        {leftTitle ? (
          <text
            x={PADDING.left - 8}
            y={PADDING.top - 9}
            textAnchor="end"
            className="fill-ink-400 text-[9px] font-semibold"
          >
            {leftTitle}
          </text>
        ) : null}
        {rightTitle ? (
          <text
            x={WIDTH - PADDING.right + 8}
            y={PADDING.top - 9}
            textAnchor="start"
            className="fill-ink-400 text-[9px] font-semibold"
          >
            {rightTitle}
          </text>
        ) : null}

        {/* Rejilla y marcas del eje izquierdo */}
        {leftScale.ticks.map((tick) => (
          <g key={`left-${tick.value}`}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={tick.y}
              y2={tick.y}
              stroke="#e6edf1"
              strokeDasharray={tick.value === 0 ? undefined : '3 4'}
            />
            <text
              x={PADDING.left - 8}
              y={tick.y + 3.5}
              textAnchor="end"
              className="fill-ink-400 text-[10px]"
            >
              {formatAxisValue(tick.value, leftFormat)}
            </text>
          </g>
        ))}

        {/* Marcas del eje derecho (sólo etiquetas, sin rejilla duplicada) */}
        {rightScale.ticks.map((tick) => (
          <text
            key={`right-${tick.value}`}
            x={WIDTH - PADDING.right + 8}
            y={tick.y + 3.5}
            textAnchor="start"
            className="fill-ink-400 text-[10px]"
          >
            {formatAxisValue(tick.value, rightFormat)}
          </text>
        ))}

        {/* Etiquetas del eje X */}
        {visibleLabels.map((index) => (
          <text
            key={index}
            x={PADDING.left + bandWidth * (index + 0.5)}
            y={height - 10}
            textAnchor="middle"
            className="fill-ink-400 text-[10px]"
          >
            {formatAxisLabel(labels[index])}
          </text>
        ))}

        {/* Zonas de interacción */}
        {labels.map((label, index) => (
          <g
            key={`band-${String(label)}-${index}`}
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            <rect
              x={PADDING.left + bandWidth * index}
              y={PADDING.top}
              width={bandWidth}
              height={innerHeight}
              fill={hover === index ? '#f4f7f9' : 'transparent'}
            />
          </g>
        ))}

        {/* Barras */}
        {bars.map((item) => (
          <g key={item.label}>
            {item.rects.map((rect, index) =>
              rect ? (
                <rect
                  key={`${item.label}-${index}`}
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  fill={item.color}
                  rx={3}
                  opacity={hover === null || hover === index ? 1 : 0.55}
                  className="transition-opacity duration-150"
                />
              ) : null,
            )}
          </g>
        ))}

        {/* Líneas */}
        {lines.map((item) => (
          <g key={item.label}>
            <path
              d={item.path}
              fill="none"
              stroke={item.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              className="[vector-effect:non-scaling-stroke]"
            />
            {item.points.map((point, index) => {
              if (point.y === null) return null;

              return (
                <circle
                  key={`${item.label}-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={hover === index ? 4.5 : 3}
                  fill="#ffffff"
                  stroke={item.color}
                  strokeWidth={2}
                />
              );
            })}
          </g>
        ))}

        {/* Guía vertical al pasar el cursor */}
        {hover !== null ? (
          <line
            x1={PADDING.left + bandWidth * (hover + 0.5)}
            x2={PADDING.left + bandWidth * (hover + 0.5)}
            y1={PADDING.top}
            y2={height - PADDING.bottom}
            stroke="#a0bccb"
            strokeDasharray="3 3"
          />
        ) : null}
      </svg>

      {/* Tooltip con altura reservada: si apareciera/desapareciera de golpe,
          cambiaría el alto del contenido y la gráfica "saltaría" al mover el
          cursor (sobre todo con `justify-center` en la tarjeta). */}
      <div className="mt-1.5 min-h-7">
        {hover !== null && labels[hover] !== undefined ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-ink-50 px-2.5 py-1.5 text-[11px]">
            <span className="font-semibold text-ink-700">{shortenLabel(labels[hover], 22)}</span>

            {series.map((item) => (
              <span key={item.label} className="flex items-center gap-1.5 text-ink-600">
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full"
                  style={{ backgroundColor: colorOf(item) }}
                />
                {item.label}:{' '}
                <strong className="font-mono font-semibold text-ink-800">
                  {formatMetricValue(
                    toFinite(item.data[hover]) ?? 0,
                    item.axis === 'left' ? leftFormat : rightFormat,
                  )}
                </strong>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}
